const abokReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const abokDesktopMotion = window.matchMedia('(min-width: 990px)');
const clampMotionProgress = (value) => Math.min(1, Math.max(0, value));

class AbokProductStoryMotion extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.revealObserver = null;
    this.progressObserver = null;
    this.progressRafIds = new Set();
    this.refreshReveals = this.refreshReveals.bind(this);
    abokReducedMotion.addEventListener('change', this.refreshReveals, {
      signal: this.abortController.signal,
    });
    this.refreshReveals();
  }

  disconnectedCallback() {
    this.revealObserver?.disconnect();
    this.progressObserver?.disconnect();
    this.progressRafIds.forEach((rafId) => cancelAnimationFrame(rafId));
    this.abortController?.abort();
  }

  refreshReveals() {
    this.revealObserver?.disconnect();
    const items = [...this.querySelectorAll('[data-abok-story-reveal]')];
    this.classList.add('is-motion-ready');
    if (abokReducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
    } else if (items.length) {
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            this.revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
      );
      items.forEach((item) => this.revealObserver.observe(item));
    }
    this.refreshProgress();
  }

  refreshProgress() {
    this.progressObserver?.disconnect();
    this.progressRafIds.forEach((rafId) => cancelAnimationFrame(rafId));
    this.progressRafIds.clear();
    const metrics = [...this.querySelectorAll('[data-abok-story-progress]')];
    if (!metrics.length) return;

    metrics.forEach((metric) => {
      metric.style.setProperty('--abok-progress-duration', `${metric.dataset.duration || 1000}ms`);
    });
    if (abokReducedMotion.matches || !('IntersectionObserver' in window)) {
      metrics.forEach((metric) => this.completeProgress(metric, false));
      return;
    }

    metrics.forEach((metric) => {
      metric.classList.remove('is-complete');
      const output = metric.querySelector('[data-abok-progress-value]');
      if (output) output.textContent = `0${metric.dataset.unit || ''}`;
    });
    this.progressObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.completeProgress(entry.target, true);
          this.progressObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.35 },
    );
    metrics.forEach((metric) => this.progressObserver.observe(metric));
  }

  completeProgress(metric, animateValue) {
    const output = metric.querySelector('[data-abok-progress-value]');
    const target = Number.parseFloat(metric.dataset.value || '0');
    const unit = metric.dataset.unit || '';
    const duration = Number.parseInt(metric.dataset.duration || '1000', 10);
    metric.classList.add('is-complete');
    if (!output || !animateValue || target <= 0) {
      if (output) output.textContent = `${target}${unit}`;
      return;
    }

    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      maximumFractionDigits: Number.isInteger(target) ? 0 : 1,
    });
    const startedAt = performance.now();
    let rafId = null;
    const tick = (now) => {
      if (rafId !== null) this.progressRafIds.delete(rafId);
      const progress = clampMotionProgress((now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 4);
      output.textContent = `${formatter.format(target * eased)}${unit}`;
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
        this.progressRafIds.add(rafId);
      } else {
        output.textContent = `${formatter.format(target)}${unit}`;
      }
    };
    rafId = requestAnimationFrame(tick);
    this.progressRafIds.add(rafId);
  }
}

class AbokFrameSequence extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    this.context = this.canvas?.getContext('2d');
    this.frameScript = this.querySelector('[data-abok-motion-frames]');
    this.steps = [...this.querySelectorAll('[data-motion-step]')];
    this.framePromises = new Map();
    this.loadedFrames = new Map();
    this.activeFrame = -1;
    this.activeStep = -1;
    this.rafId = null;
    this.isIntersecting = false;

    try {
      this.frameUrls = JSON.parse(this.frameScript?.textContent || '[]');
    } catch (_error) {
      this.frameUrls = [];
    }

    this.abortController = new AbortController();
    this.scheduleUpdate = this.scheduleUpdate.bind(this);
    this.refreshMode = this.refreshMode.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
    const signal = this.abortController.signal;

    abokReducedMotion.addEventListener('change', this.refreshMode, { signal });
    abokDesktopMotion.addEventListener('change', this.refreshMode, { signal });
    window.addEventListener('scroll', this.scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.handleResize, { passive: true, signal });

    if ('ResizeObserver' in window && this.canvas) {
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.resizeObserver.observe(this.canvas);
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
        rootMargin: '60% 0px',
      });
      this.intersectionObserver.observe(this);
    } else {
      this.isIntersecting = true;
    }

    this.refreshMode();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.framePromises?.clear();
    this.loadedFrames?.clear();
  }

  onIntersection(entries) {
    this.isIntersecting = entries.some((entry) => entry.isIntersecting);
    if (this.isIntersecting) this.scheduleUpdate();
  }

  handleResize() {
    this.refreshMode();
    this.scheduleUpdate();
  }

  refreshMode() {
    const minimumWidth = Number.parseInt(this.dataset.motionMinWidth || '990', 10);
    const canAnimate =
      Boolean(this.context) &&
      this.frameUrls.length > 1 &&
      window.innerWidth >= minimumWidth &&
      !abokReducedMotion.matches;
    this.toggleAttribute('data-motion-disabled', !canAnimate);
    if (canAnimate) {
      this.scheduleUpdate();
    } else {
      this.classList.remove('is-frame-ready');
    }
  }

  scheduleUpdate() {
    if (this.rafId || this.hasAttribute('data-motion-disabled') || !this.isIntersecting) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.updateFrame();
    });
  }

  updateFrame() {
    const rect = this.getBoundingClientRect();
    const travel = Math.max(1, rect.height - window.innerHeight);
    const progress = clampMotionProgress(-rect.top / travel);
    const frameIndex = Math.round(progress * (this.frameUrls.length - 1));

    this.updateStep(progress);
    this.loadFrame(frameIndex).then((image) => {
      if (frameIndex !== this.activeFrame) return;
      if (!image) {
        this.classList.add('is-frame-error');
        return;
      }
      this.classList.remove('is-frame-error');
      this.drawFrame(image);
    });
    this.activeFrame = frameIndex;

    [frameIndex - 1, frameIndex + 1].forEach((nearbyIndex) => {
      if (nearbyIndex >= 0 && nearbyIndex < this.frameUrls.length) this.loadFrame(nearbyIndex);
    });
  }

  loadFrame(index) {
    if (this.loadedFrames.has(index)) return Promise.resolve(this.loadedFrames.get(index));
    if (this.framePromises.has(index)) return this.framePromises.get(index);

    const promise = new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        this.loadedFrames.set(index, image);
        resolve(image);
      };
      image.onerror = () => resolve(null);
      image.src = this.frameUrls[index];
    });
    this.framePromises.set(index, promise);
    promise.finally(() => this.framePromises.delete(index));
    return promise;
  }

  drawFrame(image) {
    const width = Math.max(1, Math.round(this.canvas.clientWidth));
    const height = Math.max(1, Math.round(this.canvas.clientHeight));
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const targetWidth = Math.round(width * scale);
    const targetHeight = Math.round(height * scale);

    if (this.canvas.width !== targetWidth || this.canvas.height !== targetHeight) {
      this.canvas.width = targetWidth;
      this.canvas.height = targetHeight;
    }

    const configuredWidth = Number.parseFloat(this.dataset.frameWidth || '');
    const configuredHeight = Number.parseFloat(this.dataset.frameHeight || '');
    let drawWidth;
    let drawHeight;
    if (Number.isFinite(configuredWidth) && Number.isFinite(configuredHeight)) {
      const contentScale = Math.min(1, width / configuredWidth, height / configuredHeight);
      drawWidth = configuredWidth * contentScale * scale;
      drawHeight = configuredHeight * contentScale * scale;
    } else {
      const fit = this.dataset.frameFit === 'cover' ? Math.max : Math.min;
      const ratio = fit(targetWidth / image.naturalWidth, targetHeight / image.naturalHeight);
      drawWidth = image.naturalWidth * ratio;
      drawHeight = image.naturalHeight * ratio;
    }
    const x = (targetWidth - drawWidth) / 2;
    const y = (targetHeight - drawHeight) / 2;

    this.context.clearRect(0, 0, targetWidth, targetHeight);
    this.context.drawImage(image, x, y, drawWidth, drawHeight);
    this.classList.add('is-frame-ready');
  }

  updateStep(progress) {
    if (!this.steps.length) return;
    let nextStep = 0;
    this.steps.forEach((step, index) => {
      const threshold = Number.parseFloat(step.dataset.progress || '0');
      if (progress >= threshold) nextStep = index;
    });
    if (nextStep === this.activeStep) return;
    this.activeStep = nextStep;
    this.steps.forEach((step, index) => {
      if (index === nextStep) {
        step.setAttribute('aria-current', 'step');
      } else {
        step.removeAttribute('aria-current');
      }
    });
  }
}

class AbokStepStory extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('[data-motion-step]')];
    this.media = [...this.querySelectorAll('[data-motion-media]')];
    this.counter = this.querySelector('[data-motion-counter]');
    this.activeStep = -1;
    this.rafId = null;
    this.isIntersecting = false;
    this.abortController = new AbortController();
    this.scheduleUpdate = this.scheduleUpdate.bind(this);
    this.refreshMode = this.refreshMode.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
    const signal = this.abortController.signal;

    abokReducedMotion.addEventListener('change', this.refreshMode, { signal });
    abokDesktopMotion.addEventListener('change', this.refreshMode, { signal });
    window.addEventListener('scroll', this.scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.scheduleUpdate, { passive: true, signal });

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
        rootMargin: '40% 0px',
      });
      this.intersectionObserver.observe(this);
    } else {
      this.isIntersecting = true;
    }
    this.refreshMode();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.intersectionObserver?.disconnect();
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  onIntersection(entries) {
    this.isIntersecting = entries.some((entry) => entry.isIntersecting);
    if (this.isIntersecting) this.scheduleUpdate();
  }

  refreshMode() {
    const canAnimate = this.steps.length > 1 && abokDesktopMotion.matches && !abokReducedMotion.matches;
    this.toggleAttribute('data-motion-disabled', !canAnimate);
    if (canAnimate) {
      this.scheduleUpdate();
    } else {
      this.setActiveStep(0);
    }
  }

  scheduleUpdate() {
    if (this.rafId || this.hasAttribute('data-motion-disabled') || !this.isIntersecting) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      const rect = this.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      const progress = clampMotionProgress(-rect.top / travel);
      const index = Math.min(this.steps.length - 1, Math.floor(progress * this.steps.length));
      this.setActiveStep(index);
    });
  }

  setActiveStep(index) {
    if (index === this.activeStep || !this.steps.length) return;
    this.activeStep = index;
    this.style.setProperty('--abok-motion-step', String(index));
    this.steps.forEach((step, stepIndex) => {
      if (stepIndex === index) {
        step.setAttribute('aria-current', 'step');
      } else {
        step.removeAttribute('aria-current');
      }
    });
    this.media.forEach((media, mediaIndex) => {
      media.setAttribute('aria-hidden', String(mediaIndex !== index));
    });
    if (this.counter) this.counter.textContent = String(index + 1);
  }
}

class AbokGradientCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-abok-gradient-slide]')];
    this.activeSlide = -1;
    this.rafId = null;
    this.abortController = new AbortController();
    this.scheduleUpdate = this.scheduleUpdate.bind(this);
    this.refreshMode = this.refreshMode.bind(this);
    const signal = this.abortController.signal;

    window.addEventListener('scroll', this.scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.refreshMode, { passive: true, signal });
    abokReducedMotion.addEventListener('change', this.refreshMode, { signal });
    this.refreshMode();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  refreshMode() {
    const canAnimate = this.slides.length > 1 && window.innerWidth >= 1300 && !abokReducedMotion.matches;
    this.toggleAttribute('data-motion-disabled', !canAnimate);
    this.toggleAttribute('data-motion-ready', canAnimate);
    if (canAnimate) {
      this.scheduleUpdate();
    } else {
      this.activeSlide = -1;
      this.slides.forEach((slide) => {
        slide.classList.add('is-active');
        slide.setAttribute('aria-hidden', 'false');
      });
    }
  }

  scheduleUpdate() {
    if (this.rafId || this.hasAttribute('data-motion-disabled')) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      const rect = this.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      const progress = clampMotionProgress(-rect.top / travel);
      const index = Math.min(this.slides.length - 1, Math.floor(progress * this.slides.length));
      this.setActiveSlide(index);
    });
  }

  setActiveSlide(index) {
    if (index === this.activeSlide) return;
    this.activeSlide = index;
    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
  }
}

class AbokProductVideo extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    const playbackRate = Number.parseFloat(video.dataset.abokPlaybackRate || '1');
    if (Number.isFinite(playbackRate) && playbackRate > 0) video.playbackRate = playbackRate;
    if (video.autoplay) video.play().catch(() => {});
  }
}

if (!customElements.get('abok-product-story-motion')) {
  customElements.define('abok-product-story-motion', AbokProductStoryMotion);
}
if (!customElements.get('abok-frame-sequence')) {
  customElements.define('abok-frame-sequence', AbokFrameSequence);
}
if (!customElements.get('abok-step-story')) {
  customElements.define('abok-step-story', AbokStepStory);
}
if (!customElements.get('abok-gradient-carousel')) {
  customElements.define('abok-gradient-carousel', AbokGradientCarousel);
}
if (!customElements.get('abok-product-video')) {
  customElements.define('abok-product-video', AbokProductVideo);
}
